<template>
  <a-modal
    :visible="visible"
    :title="mode === 'edit' ? '编辑 Tab' : '新增 Tab'"
    width="720px"
    :destroy-on-close="true"
    :body-style="{ maxHeight: 'calc(100vh - 260px)', overflowY: 'auto' }"
    :confirm-loading="confirmLoading"
    ok-text="提交"
    cancel-text="取消"
    @ok="handleSubmit"
    @cancel="handleCancel"
  >
    <p class="modal-description">带 * 为必填</p>

    <a-tabs v-model:activeKey="activeLanguage" class="language-tabs">
      <a-tab-pane
        v-for="(language, index) in languages"
        :key="language.value"
        :force-render="true"
      >
        <template #tab>
          <span>
            {{ language.label }}
            <a-badge v-if="hasLanguageError(index)" status="error" />
          </span>
        </template>
        <div class="field-block">
          <label>标题（{{ language.label }}）<em>*</em></label>
          <a-input
            v-model:value="form.languages[index].title"
            :maxlength="15"
            show-count
            placeholder="最多 15 字符"
          />
        </div>
        <div v-if="form.materialType === 3" class="field-block">
          <label>展示文字（{{ language.label }}）<em>*</em></label>
          <a-input
            v-model:value="form.languages[index].displayText"
            :maxlength="15"
            show-count
            placeholder="最多 15 字符"
          />
        </div>
      </a-tab-pane>
    </a-tabs>

    <div class="field-block">
      <label>类型 <em>*</em></label>
      <div class="option-grid">
        <button
          v-for="item in materialTypeOptions"
          :key="item.value"
          type="button"
          class="option-card"
          :class="{ selected: form.materialType === item.value }"
          :disabled="item.disabled"
          @click="changeMaterialType(item.value)"
        >
          <component :is="item.icon" />
          <strong>{{ item.label }}</strong>
          <span>{{ item.description }}</span>
        </button>
      </div>
    </div>

    <div class="field-block">
      <label>
        上传图标 <em>*</em>
        <small>{{
          form.materialType === 2 ? '尺寸 40 × 40' : '尺寸 22 × 22'
        }}</small>
      </label>
      <div v-if="form.materialType === 2" class="upload-grid single">
        <ImageUploadDragger
          v-model:file-list="selectedIconFiles"
          :material-type="form.materialType"
          label="大图"
        />
      </div>
      <div v-else class="upload-grid">
        <ImageUploadDragger
          v-model:file-list="unselectedIconFiles"
          :material-type="form.materialType"
          label="选中前"
        />
        <ImageUploadDragger
          v-model:file-list="selectedIconFiles"
          :material-type="form.materialType"
          label="选中后"
        />
      </div>
    </div>

    <div class="field-block">
      <label>跳转类型 <em>*</em></label>
      <div class="option-grid route-option-grid">
        <button
          v-for="item in jumpTypeOptions"
          :key="item.value"
          type="button"
          class="option-card compact"
          :class="{ selected: form.jumpType === item.value }"
          @click="changeJumpType(item.value)"
        >
          <component :is="item.icon" />
          <strong>{{ item.label }}</strong>
        </button>
      </div>
      <a-input
        v-if="form.jumpType === 1"
        v-model:value="form.h5Url"
        class="conditional-control"
        placeholder="请输入 https:// 开头的 H5 链接"
      />
      <a-select
        v-else
        v-model:value="form.routeId"
        class="conditional-control"
        placeholder="请选择内部路由"
        :options="routeOptions"
        :loading="routeLoading"
      />
    </div>

    <div class="field-block color-field">
      <label>Tab 选中后背景色 <em>*</em></label>
      <NColorPicker
        v-model:value="form.selectedBackgroundColor"
        :modes="['hex']"
        :show-alpha="false"
      />
    </div>

    <div v-if="form.materialType === 3" class="text-color-grid">
      <div class="field-block color-field">
        <label>选中文字颜色 <em>*</em></label>
        <NColorPicker
          v-model:value="form.selectedTextColor"
          :modes="['hex']"
          :show-alpha="false"
        />
      </div>
      <div class="field-block color-field">
        <label>未选中文字颜色 <em>*</em></label>
        <NColorPicker
          v-model:value="form.unselectedTextColor"
          :modes="['hex']"
          :show-alpha="false"
        />
      </div>
    </div>
  </a-modal>
</template>

<script>
import { computed, reactive, ref, watch } from 'vue'
import {
  AppstoreOutlined,
  CompassOutlined,
  FontSizeOutlined,
  LinkOutlined,
  PictureOutlined
} from '@ant-design/icons-vue'
import { message } from 'ant-design-vue'
import { NColorPicker } from 'naive-ui'
import { appTab_material_routes_get } from '@/server/modules/25824.js'
import ImageUploadDragger from './ImageUploadDragger.vue'

const getLanguageTitle = (record, language) => {
  const currentLanguage = record?.languages?.find(item =>
    [item.langCode, item.languageCode, item.languageId, item.code].includes(
      language.value
    )
  )
  return (
    currentLanguage?.title ||
    currentLanguage?.materialName ||
    currentLanguage?.name ||
    ''
  )
}

const getLanguageDisplayText = (record, language) => {
  const currentLanguage = record?.languages?.find(item =>
    [item.langCode, item.languageCode, item.languageId, item.code].includes(
      language.value
    )
  )
  return currentLanguage?.displayText || ''
}

const createForm = (record, languages = []) => ({
  languages: languages.map(language => ({
    langCode: language.value,
    title: getLanguageTitle(record, language),
    displayText: getLanguageDisplayText(record, language)
  })),
  materialType: record?.materialType ?? 1,
  jumpType: record?.jumpType ?? 2,
  h5Url: record?.h5Url || '',
  routeId: record?.routeId,
  selectedBackgroundColor: record?.selectedBackgroundColor || '#FFFFFF',
  selectedIconUrl: record?.selectedIconUrl || '',
  unselectedIconUrl: record?.unselectedIconUrl || '',
  selectedTextColor: record?.selectedTextColor || '#000000',
  unselectedTextColor: record?.unselectedTextColor || '#595959'
})

const imageRatioRequirements = {
  1: { width: 1, height: 1, label: '1:1' },
  2: { width: 1, height: 1, label: '1:1' },
  3: { width: 1, height: 1, label: '1:1' }
}

const createImageSize = () => ({
  width: undefined,
  height: undefined,
  uploaded: false
})

const createFileList = (url, imageSize) =>
  url
    ? [
        {
          uid: url,
          name: 'image',
          status: 'done',
          url,
          width: imageSize.width,
          height: imageSize.height,
          uploaded: imageSize.uploaded
        }
      ]
    : []

const isImageRatioValid = (imageSize, materialType) => {
  const requirement = imageRatioRequirements[materialType]
  if (!requirement || !imageSize.width || !imageSize.height) return false
  return (
    imageSize.width * requirement.height ===
    imageSize.height * requirement.width
  )
}

export default {
  components: {
    ImageUploadDragger,
    NColorPicker
  },
  props: {
    visible: Boolean,
    mode: {
      type: String,
      default: 'create'
    },
    record: {
      type: Object,
      default: null
    },
    languages: {
      type: Array,
      default: () => []
    },
    confirmLoading: Boolean
  },
  emits: ['update:visible', 'submit'],
  setup(props, { emit }) {
    const activeLanguage = ref('en')
    const form = reactive(createForm(null, props.languages))
    const routeLoading = ref(false)
    const routeLoaded = ref(false)
    const routeOptions = ref([])
    const selectedIconSize = reactive(createImageSize())
    const unselectedIconSize = reactive(createImageSize())
    const materialTypeOptions = [
      {
        label: 'icon',
        value: 1,
        description: '22 × 22',
        icon: AppstoreOutlined
      },
      {
        label: '大图',
        value: 2,
        description: '40 × 40 · 栏位最多 1 个',
        icon: PictureOutlined,
        disabled: true
      },
      {
        label: 'icon+文字',
        value: 3,
        description: '22 × 22 + 文案',
        icon: FontSizeOutlined
      }
    ]
    const jumpTypeOptions = [
      { label: 'H5 链接', value: 1, icon: LinkOutlined },
      { label: '路由', value: 2, icon: CompassOutlined }
    ]
    const selectedIconFiles = computed({
      get: () => createFileList(form.selectedIconUrl, selectedIconSize),
      set: files => {
        const file = files[0]
        form.selectedIconUrl = file?.url || ''
        Object.assign(
          selectedIconSize,
          file
            ? {
                width: Number(file.width ?? file.response?.width) || undefined,
                height:
                  Number(file.height ?? file.response?.height) || undefined,
                uploaded: Boolean(file.uploaded)
              }
            : createImageSize()
        )
      }
    })
    const unselectedIconFiles = computed({
      get: () => createFileList(form.unselectedIconUrl, unselectedIconSize),
      set: files => {
        const file = files[0]
        form.unselectedIconUrl = file?.url || ''
        Object.assign(
          unselectedIconSize,
          file
            ? {
                width: Number(file.width ?? file.response?.width) || undefined,
                height:
                  Number(file.height ?? file.response?.height) || undefined,
                uploaded: Boolean(file.uploaded)
              }
            : createImageSize()
        )
      }
    })

    const resetForm = () => {
      Object.assign(form, createForm(props.record, props.languages))
      Object.assign(selectedIconSize, createImageSize())
      Object.assign(unselectedIconSize, createImageSize())
      activeLanguage.value = props.languages[0]?.value
    }
    const loadRouteOptions = async () => {
      if (routeLoaded.value || routeLoading.value) return

      routeLoading.value = true
      try {
        const { success, data, retInfo } = await appTab_material_routes_get()
        if (!success) {
          message.error(retInfo || 'APP Tab 路由获取失败')
          return
        }

        routeOptions.value = Array.isArray(data)
          ? data.map(item => ({
              label: item.routeName || item.routeUrl || `${item.id}`,
              value: item.id
            }))
          : []
        routeLoaded.value = true
        if (form.jumpType === 2 && !form.routeId) {
          form.routeId = routeOptions.value[0]?.value
        }
      } catch (error) {
        message.error(error?.message || 'APP Tab 路由获取失败')
      } finally {
        routeLoading.value = false
      }
    }
    const changeMaterialType = type => {
      if (form.materialType === type) return

      form.materialType = type
      form.selectedIconUrl = ''
      form.unselectedIconUrl = ''
      Object.assign(selectedIconSize, createImageSize())
      Object.assign(unselectedIconSize, createImageSize())
    }
    const changeJumpType = type => {
      form.jumpType = type
      form.h5Url = ''
      form.routeId = type === 2 ? routeOptions.value[0]?.value : undefined
    }
    const handleCancel = () => {
      emit('update:visible', false)
    }
    const hasLanguageError = index => {
      const language = form.languages[index]
      return (
        !language?.title?.trim() ||
        (form.materialType === 3 && !language?.displayText?.trim())
      )
    }
    const validateUploadedImageRatio = (imageSize, imageLabel) => {
      if (!imageSize.uploaded) return true
      if (!imageSize.width || !imageSize.height) {
        message.error(`${imageLabel}未获取到图片尺寸，请重新上传`)
        return false
      }
      if (!isImageRatioValid(imageSize, form.materialType)) {
        const requirement = imageRatioRequirements[form.materialType]
        message.error(
          `${imageLabel}宽高比例需为 ${requirement.label}，当前尺寸为 ${imageSize.width} × ${imageSize.height}`
        )
        return false
      }
      return true
    }
    const handleSubmit = () => {
      if (!props.languages.length) {
        return message.error('当前站点暂无可用语言')
      }
      const missingLanguageIndex = props.languages.findIndex(
        (language, index) => !form.languages[index]?.title?.trim()
      )
      if (missingLanguageIndex !== -1) {
        const missingLanguage = props.languages[missingLanguageIndex]
        activeLanguage.value = missingLanguage.value
        return message.error(`请填写标题（${missingLanguage.label}）`)
      }
      if (form.materialType === 3) {
        const missingDisplayTextIndex = props.languages.findIndex(
          (language, index) => !form.languages[index]?.displayText?.trim()
        )
        if (missingDisplayTextIndex !== -1) {
          const missingLanguage = props.languages[missingDisplayTextIndex]
          activeLanguage.value = missingLanguage.value
          return message.error(`请填写展示文字（${missingLanguage.label}）`)
        }
      }
      if (!form.selectedIconUrl) {
        return message.error(
          form.materialType === 2 ? '请上传大图' : '请上传选中后图标'
        )
      }
      if (form.materialType !== 2 && !form.unselectedIconUrl) {
        return message.error('请上传选中前图标')
      }
      if (
        !validateUploadedImageRatio(
          selectedIconSize,
          form.materialType === 2 ? '大图' : '选中后图标'
        )
      ) {
        return
      }
      if (
        form.materialType !== 2 &&
        !validateUploadedImageRatio(unselectedIconSize, '选中前图标')
      ) {
        return
      }
      if (!form.selectedBackgroundColor) {
        return message.error('请选择 Tab 选中后背景色')
      }
      if (
        form.materialType === 3 &&
        (!form.selectedTextColor || !form.unselectedTextColor)
      ) {
        return message.error('请选择选中和未选中的文字颜色')
      }
      if (form.jumpType === 1 && !form.h5Url.trim()) {
        return message.error('请填写 H5 链接')
      }
      if (form.jumpType === 2 && !form.routeId) {
        return message.error('请填写或选择跳转目标')
      }
      emit('submit', {
        ...form,
        h5Url: form.jumpType === 1 ? form.h5Url.trim() : undefined,
        routeId: form.jumpType === 2 ? form.routeId : undefined,
        selectedTextColor:
          form.materialType === 3 ? form.selectedTextColor : undefined,
        unselectedIconUrl:
          form.materialType === 2
            ? form.selectedIconUrl
            : form.unselectedIconUrl,
        unselectedTextColor:
          form.materialType === 3 ? form.unselectedTextColor : undefined,
        languages: form.languages.map(item => ({
          langCode: item.langCode,
          title: item.title.trim(),
          displayText:
            form.materialType === 3 ? item.displayText.trim() : undefined
        }))
      })
    }

    watch(
      () => props.visible,
      visible => {
        if (visible) {
          resetForm()
          loadRouteOptions()
        }
      }
    )

    return {
      activeLanguage,
      changeJumpType,
      changeMaterialType,
      form,
      hasLanguageError,
      handleCancel,
      handleSubmit,
      jumpTypeOptions,
      materialTypeOptions,
      routeOptions,
      routeLoading,
      selectedIconFiles,
      unselectedIconFiles
    }
  }
}
</script>

<style lang="scss" scoped>
.modal-description {
  margin: -4px 0 4px;
  color: #8c8c8c;
  font-size: 12px;
}

.language-tabs {
  margin-bottom: 14px;
}

.field-block {
  margin-bottom: 20px;

  > label {
    display: block;
    margin-bottom: 8px;
    color: #595959;
    font-weight: 500;
  }

  em {
    color: #ff4d4f;
    font-style: normal;
  }

  small {
    margin-left: 6px;
    color: #8c8c8c;
    font-weight: 400;
  }
}

.option-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
}

.route-option-grid {
  grid-template-columns: repeat(2, 1fr);
}

.option-card {
  display: flex;
  min-height: 88px;
  padding: 10px;
  border: 1px solid #d9d9d9;
  border-radius: 6px;
  background: #fff;
  color: #595959;
  cursor: pointer;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;

  :deep(.anticon) {
    color: #595959;
    font-size: 20px;
  }

  span {
    color: #8c8c8c;
    font-size: 12px;
  }

  &.compact {
    min-height: 70px;
  }

  &.selected {
    border-color: #1890ff;
    background: #e6f7ff;
    color: #1890ff;

    :deep(.anticon) {
      color: #1890ff;
    }
  }

  &:disabled {
    border-color: #f0f0f0;
    background: #fafafa;
    color: #bfbfbf;
    cursor: not-allowed;

    :deep(.anticon),
    span {
      color: #bfbfbf;
    }
  }
}

.upload-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 14px;

  &.single {
    grid-template-columns: 1fr;
  }
}

.text-color-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 14px;
}

.conditional-control {
  width: 100%;
  margin-top: 10px;
}
</style>
